import { z } from 'zod';
import { mailAnlassSchema, mailArtSchema, mailKatalogEintragSchema, mailPruefungErgebnisSchema, mailPruefungFeldSchema, } from './mailvorlagen-pruefung.js';
import { uuid } from './posting.js';
/**
 * **Die Verträge der E-Mail-Vorlagen** — Auftrag `C-mailcrud` (2026-09-02),
 * Task 3 des Plans
 * `docs/superpowers/plans/2026-09-02-email-vorlagen-umsetzung.md`, Spec
 * `docs/superpowers/specs/2026-09-02-email-vorlagen-design.md` Abschnitte 4
 * und 9.1.
 *
 * **Es gibt kein `PATCH` und kein `DELETE`, und deshalb auch kein
 * Änderungsschema** (`D-10`). Ändern heisst: eine Fassung anfügen. Was hier
 * fehlt, fehlt absichtlich — ein `mailvorlagePatchSchema` wäre der erste
 * Schritt zu einem `UPDATE`, für das `fameacc_app` auf `mailvorlage` gar kein
 * Recht hat (Migration `0130`).
 *
 * **Der Betreff wird NICHT gestrafft, und das ist der eigentliche Kniff dieser
 * Datei.** Die naheliegende Behandlung eines einzeiligen Textfeldes — trimmen
 * und innere Weissraumfolgen auf ein Leerzeichen zusammenziehen, wie es
 * `aborechnungen.ts` für seine Bezeichnungen tut — würde einen Zeilenumbruch
 * im Betreff STILL entfernen und damit genau die Eingabe unsichtbar machen,
 * die `E-717` abweisen soll (Header-Injection, Spec 6.4). Der Vertrag prüft
 * hier deshalb nur auf «nicht leer» und reicht den Text unverändert an den
 * Prüfer weiter; abgewiesen wird benannt, nie stillschweigend bereinigt —
 * dieselbe Haltung wie bei der Markup-Ablehnungsliste (`D-13`).
 *
 * **Was hier NICHT steht: die art-abhängigen Feldregeln.** Ob eine Fassung
 * einen `betreff` und einen `koerperText` tragen muss (Inhalt) oder nicht
 * tragen darf (Rahmen), hängt an der **Art des Kopfes** (`D-7`, `D-15`). Beim
 * Anfügen einer Fassung steht die Art nicht im Rumpf, sondern in der
 * vorhandenen Zeile `mailvorlage` — die Regel steht deshalb genau einmal im
 * Dienst (`mailvorlagen.service.ts`, `pruefeFelderGegenArt`) und nicht
 * zusätzlich hier. Zwei Fassungen derselben Regel wären zwei Auslegungen, und
 * die zweite ist die, die als erste falsch wird.
 */
// --- Bausteine -------------------------------------------------------------
/** Trimmt und zieht Folgen von Leerraum auf ein Leerzeichen zusammen. */
function straffen(wert) {
    return wert.trim().replace(/\s+/g, ' ');
}
/** Ein gestraffter Pflichttext — nur für Felder, in denen Weissraum nichts bedeutet. */
const pflichtText = (feld) => z.string().transform((wert, ctx) => {
    const gestrafft = straffen(wert);
    if (gestrafft === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
        return z.NEVER;
    }
    return gestrafft;
});
/**
 * Ein Pflichttext, der **unverändert** durchgereicht wird.
 *
 * Für `koerperHtml` und `betreff`: Weissraum ist dort Inhalt (HTML-Einrückung)
 * beziehungsweise Befund (`E-717`). Geprüft wird nur, dass überhaupt etwas
 * dasteht.
 */
const roherPflichtText = (feld) => z.string().superRefine((wert, ctx) => {
    if (wert.trim() === '') {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${feld} darf nicht leer sein` });
    }
});
/**
 * Ein freiwilliges Textfeld, ebenfalls unverändert durchgereicht.
 *
 * `undefined` und `null` laufen beide auf `null` zusammen — die Datenbank
 * kennt nur `NULL`, und `exactOptionalPropertyTypes` machte aus dem
 * Unterschied sonst eine Fallunterscheidung in jedem Aufrufer.
 */
const roherOptionalText = (feld) => z
    .string()
    .nullish()
    .transform((wert) => wert ?? null)
    .superRefine((wert, ctx) => {
    if (wert !== null && wert.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${feld} darf nicht leer sein, wenn es angegeben wird`,
        });
    }
});
/**
 * Der Sprachcode einer Inhaltsvorlage.
 *
 * **Zwei Kleinbuchstaben und ausdrücklich KEINE Aufzählung.** `mailvorlage.
 * sprache` zeigt auf `document_language(code)` (`D-6`), und diese Tabelle ist
 * **Daten** — sie trägt heute `de` und `en` (Migration `0019`), eine dritte
 * Sprache ist dort eine Zeile und keine Codeänderung. Ein `z.enum(['de','en'])`
 * hier wäre eine zweite, abgeschriebene Liste, die beim Hinzufügen der dritten
 * Sprache still falsch würde. Ein unbekannter Code läuft deshalb in den
 * Fremdschlüssel und kommt als `MAILVORLAGE_SPRACHE_UNBEKANNT` zurück.
 */
const spracheCode = z
    .string()
    .regex(/^[a-z]{2}$/, 'Ein Sprachcode besteht aus zwei Kleinbuchstaben');
// --- Aufzählungen ----------------------------------------------------------
/**
 * Die vier Zustände einer Fassung (`mailvorlage_fassung_status_bekannt`).
 *
 * Als LESESCHEMA vollständig. Als ZIEL gibt es sie nirgends: Die Übergänge
 * haben eigene Endpunkte (`…/scharfschalten`, `…/verwerfen`), und `abgeloest`
 * setzt ausschliesslich der Dienst beim Scharfschalten der Nachfolgerin. Ein
 * Statusfeld im Rumpf gäbe es nicht — dieselbe Bauform wie beim Anlagevertrag
 * der Aborechnungen (`E-656`).
 */
export const mailvorlageStatusSchema = z.enum([
    'entwurf',
    'scharf',
    'abgeloest',
    'verworfen',
]);
// --- Eingaben --------------------------------------------------------------
/** Der Text einer Fassung, so wie ein Aufrufer ihn schickt. */
export const mailvorlageFassungEingabeSchema = z
    .object({
    betreff: roherOptionalText('betreff'),
    koerperHtml: roherPflichtText('koerperHtml'),
    koerperText: roherOptionalText('koerperText'),
})
    .strict();
/**
 * Eine Vorlage anlegen — Kopf und erste Fassung in einem Aufruf.
 *
 * **Kein Statusfeld** (`D-10`): Die erste Fassung ist immer ein `entwurf`.
 * Wer sie scharf anlegen könnte, umginge `E-729` — die Prüfung, dass eine
 * Inhaltsvorlage ohne scharfen Rahmen nicht scharf werden darf.
 */
export const mailvorlageAnlageSchema = z
    .object({
    art: mailArtSchema,
    anlass: mailAnlassSchema.nullish().transform((wert) => wert ?? null),
    sprache: spracheCode.nullish().transform((wert) => wert ?? null),
    bezeichnung: pflichtText('bezeichnung'),
    fassung: mailvorlageFassungEingabeSchema,
})
    .strict()
    .superRefine((wert, ctx) => {
    // Der Spiegel von `mailvorlage_art_felder` (`E-707`), BEIDSEITIG wie dort.
    // Die Bedingung in der Datenbank bleibt die massgebliche — sie gilt auch
    // gegen direktes SQL; diese Zeilen sagen dem Aufrufer nur, was ihm fehlt,
    // bevor die Datenbank es unverständlich sagt.
    if (wert.art === 'rahmen' && (wert.anlass !== null || wert.sprache !== null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Ein Rahmen trägt weder Anlass noch Sprache: Er ist das eine Standard-Design und hat keinen eigenen Text, der übersetzt werden müsste.',
        });
    }
    if (wert.art === 'inhalt' && (wert.anlass === null || wert.sprache === null)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Eine Inhaltsvorlage trägt einen Anlass UND eine Sprache.',
        });
    }
});
/**
 * Der Rumpf der Vorschau — **ungespeicherter** Text (Spec 8.1).
 *
 * Er trägt die Art mit, weil es die Zeile in `mailvorlage` noch nicht geben
 * muss: Die Vorschau soll zeigen, was gerade getippt wird, bevor gespeichert
 * wird. Der Katalog hängt an der Art (`katalogFuer`), und ohne sie liesse sich
 * gar nicht prüfen.
 */
export const mailvorlageVorschauEingabeSchema = z
    .object({
    art: mailArtSchema,
    anlass: mailAnlassSchema.nullish().transform((wert) => wert ?? null),
    betreff: roherOptionalText('betreff'),
    koerperHtml: roherPflichtText('koerperHtml'),
    koerperText: roherOptionalText('koerperText'),
    /**
     * Der Rahmentext, in den der Inhalt eingesetzt wird — **freiwillig**, und
     * ausdrücklich als TEXT und nicht als Kennung einer Fassung
     * (`C-mailvorschau`, `D-5`).
     *
     * **Warum der Aufrufer ihn mitschickt und der Server ihn nicht nachliest.**
     * Die Vorschau zeigt UNGESPEICHERTEN Text — das ist ihr ganzer Zweck —, und
     * das gilt für den Rahmen genauso wie für den Inhalt: Wer das
     * Standard-Design gerade fortschreibt, will den neuen Rahmen sehen und
     * nicht den scharfen. Eine Fassungskennung könnte das nicht ausdrücken.
     * Nebenbei bleibt die Vorschau damit **ohne jede Abfrage** und kann
     * strukturell nichts schreiben (`E-725`); den scharfen Rahmentext hat der
     * Aufrufer ohnehin aus `GET /api/v1/mailvorlagen`, wo die scharfe Fassung
     * mit vollem Text steht.
     *
     * Fehlt er bei `art: "inhalt"`, zeigt die Vorschau den Inhalt **ohne
     * Hülle**; die Antwort sagt das unter `rahmenQuelle`.
     */
    rahmenHtml: roherOptionalText('rahmenHtml'),
})
    .strict()
    .superRefine((wert, ctx) => {
    // Bei `art: "rahmen"` IST `koerperHtml` der Rahmen. Ein zweiter daneben
    // wäre zwei Rahmen in einer Anfrage, und keine Reihenfolge wäre die
    // erwartete — abgewiesen statt stillschweigend einer von beiden gewählt.
    if (wert.art === 'rahmen' && wert.rahmenHtml !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Bei art: "rahmen" ist koerperHtml selbst der Rahmen; ein zusätzliches rahmenHtml wären zwei Rahmen in einer Anfrage.',
        });
    }
});
// --- Prüfbefunde -----------------------------------------------------------
/**
 * Das Prüfergebnis EINES Feldes.
 *
 * **Je Feld und nicht als eine gemeinsame Liste**, weil eine Oberfläche das
 * Feld markieren soll, in dem der Tippfehler steht.
 * `MailPruefungBefund` (`C-mailkatalog`) trägt seinen Pfad, aber nicht, aus
 * welchem der drei Felder er stammt — und der Prüfer bekommt Feld für Feld
 * genau einen Text. Diese Hülle ist die Stelle, an der die Zuordnung entsteht;
 * sie ändert an `C-mailkatalog` nichts.
 */
export const mailvorlageFeldPruefungSchema = z.object({
    feld: mailPruefungFeldSchema,
    ergebnis: mailPruefungErgebnisSchema,
});
/**
 * Woher der Rahmen kam, in den der Inhalt eingesetzt wurde.
 *
 * Drei Fälle, und sie sind für den Leser der Vorschau nicht dasselbe:
 * `selbst` — der geprüfte Text IST der Rahmen (`art: "rahmen"`), eingesetzt
 * wurde der feste Beispielinhalt; `angegeben` — der Aufrufer hat einen
 * Rahmentext mitgeschickt; `ohne` — es wurde keiner mitgeschickt, und die
 * Vorschau zeigt den Inhalt ohne Hülle. Ohne diese Angabe sähe der dritte Fall
 * aus wie ein Rahmen ohne Gestaltung.
 */
export const mailvorlageRahmenQuelleSchema = z.enum(['selbst', 'angegeben', 'ohne']);
/**
 * Das gerenderte Ergebnis der Vorschau — was der Versand senden würde
 * (`C-mailvorschau`, Spec 8.1, `D-5`, `D-12`).
 *
 * **Gerendert wird gegen den festen Beispielbeleg** (`D-21`, `E-724`):
 * erfundener Kunde, erfundene Belegnummer, erfundene Beträge, erfundene IBAN.
 * Kein Wert daraus stammt aus dem Bestand, und es gibt kein Feld, mit dem ein
 * Aufrufer einen echten Beleg verlangen könnte.
 *
 * **`html` trägt Rahmen UND Inhalt, `text` nur den Inhalt.** Ein Rahmen führt
 * keine Textalternative (`D-15`): Die Hülle ist HTML, und ihr Tabellenlayout
 * hätte in einer Textfassung nichts zu sagen.
 */
export const mailvorlageVorschauErgebnisSchema = z.object({
    betreff: z.string().nullable(),
    html: z.string(),
    text: z.string().nullable(),
    /** Der Fassungshash über Rahmen, Inhalt, Betreff und Katalogfassung (`D-12`, `E-723`). */
    fassungshash: z.string(),
    rahmenQuelle: mailvorlageRahmenQuelleSchema,
});
/**
 * Die Antwort der Vorschau.
 *
 * **`ok` ist genau dann `true`, wenn KEIN Feld einen Befund trägt** — dieselbe
 * Zusage wie in `mailPruefungErgebnisSchema`, eine Ebene höher: Kein Aufrufer
 * soll `ok` und `felder` getrennt auswerten müssen und dabei auseinanderlaufen
 * können.
 *
 * **Seit `C-mailvorschau` (2026-09-03) steht das gerenderte Ergebnis daneben**
 * — `vorschau` und `vorschauFehler`. Die zwei Felder sind **hinzugekommen**;
 * `ok` und `felder` bedeuten unverändert dasselbe wie vorher, und kein
 * Aufrufer, der nur sie liest, ändert sich.
 */
export const mailvorlageVorschauAntwortSchema = z.object({
    ok: z.boolean(),
    felder: z.array(mailvorlageFeldPruefungSchema),
    /**
     * Das gerenderte Ergebnis — `null`, wenn die Vorlagensprache es nicht
     * herstellen konnte; `vorschauFehler` sagt dann, woran.
     */
    vorschau: mailvorlageVorschauErgebnisSchema.nullable(),
    /**
     * Warum keine Vorschau entstand, im Wortlaut der Vorlagensprache — oder
     * `null`, wenn eine entstand.
     *
     * **Ein gescheitertes Rendern ist kein 400.** Die Vorschau warnt, sie sperrt
     * nicht (Spec 8.3): Wer gerade `{{kunde.na` getippt hat, bekommt seine
     * Befunde unter `felder` und diese Zeile, nicht eine abgelehnte Anfrage.
     * Aufgehalten wird das **Speichern**, und zwar im Dienst.
     */
    vorschauFehler: z.string().nullable(),
});
// --- Ansichten -------------------------------------------------------------
/** Eine Fassung, wie die Schnittstelle sie zeigt. */
export const mailvorlageFassungAnsichtSchema = z.object({
    id: uuid,
    vorlageId: uuid,
    fassungNr: z.number().int(),
    betreff: z.string().nullable(),
    koerperHtml: z.string(),
    koerperText: z.string().nullable(),
    status: mailvorlageStatusSchema,
    pruefsumme: z.string(),
    scharfgeschaltetVon: z.string().nullable(),
    scharfgeschaltetAm: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string(),
});
/** Kopf und vollständige Fassungshistorie, jüngste Fassung zuerst. */
export const mailvorlageAnsichtSchema = z.object({
    id: uuid,
    art: mailArtSchema,
    anlass: z.string().nullable(),
    sprache: z.string().nullable(),
    bezeichnung: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    fassungen: z.array(mailvorlageFassungAnsichtSchema),
});
/**
 * Ein Listeneintrag: der Kopf und **nur** die scharfe Fassung.
 *
 * Die volle Historie liefert die Einzelauskunft. In einer Liste wüchse sie mit
 * dem Produkt aus Vorlagen und Fortschreibungen, und jede Fassung trägt ihren
 * vollen Vorlagentext — dieselbe Rechnung wie bei den Aborechnungsvorlagen,
 * nur mit deutlich grösseren Zeilen.
 */
export const mailvorlageListeEintragSchema = z.object({
    id: uuid,
    art: mailArtSchema,
    anlass: z.string().nullable(),
    sprache: z.string().nullable(),
    bezeichnung: z.string(),
    createdAt: z.string(),
    createdBy: z.string(),
    scharfeFassung: mailvorlageFassungAnsichtSchema.nullable(),
    fassungenAnzahl: z.number().int(),
});
/** Die Liste: der Rahmen zuerst, danach die Inhaltsvorlagen. */
export const mailvorlageListeSchema = z.object({
    vorlagen: z.array(mailvorlageListeEintragSchema),
});
/**
 * Der Platzhalterkatalog, wie der Editor ihn bekommt.
 *
 * **Vom Server und nicht in der Oberfläche abgeschrieben** (Spec 9.3): Eine
 * zweite Liste liefe auseinander, und der Editor böte einen Platzhalter an,
 * den der Speicherpfad abweist.
 *
 * **Ein Katalog je ART und nicht je Anlass**, obwohl die Überschrift der Spec
 * «je Art und Anlass» sagt: `INHALT_KATALOG` (`katalog.ts`) gilt für alle fünf
 * Anlässe gleich — `RenderInput` hat für sie dieselbe Form, sie unterscheiden
 * sich in der Überschrift (`CAPTIONS`) und in nichts, was der Katalog sähe.
 * `anlaesse` steht deshalb als eigene Liste daneben: Der Editor braucht sie
 * für seine Auswahl, nicht für die Platzhalterleiste.
 */
export const mailPlatzhalterAntwortSchema = z.object({
    rahmen: z.array(mailKatalogEintragSchema),
    inhalt: z.array(mailKatalogEintragSchema),
    anlaesse: z.array(mailAnlassSchema),
    /** Die Einsetzstelle des Rahmens, genau einmal (`E-718`) — kein Katalogpfad. */
    inhaltPlatzhalter: z.string(),
});
//# sourceMappingURL=mailvorlagen.js.map